import { tokenStore } from '@/lib/auth/tokenStore'
import { getPortalApiBase } from '@/lib/env'

/**
 * Cliente del API de difusión por WhatsApp de las notas de versión (goal
 * `difusion-de-novedades 2`).
 *
 * Los endpoints exigen Bearer real de Identidad **y** membresía activa en
 * `admin_users` — el mismo gate que el resto de la administración del portal.
 * El `<Can permission="landing:edit">` de la ruta solo esconde el menú.
 */
const ENDPOINT = `${getPortalApiBase()}/broadcast`

/** Estados que anota el registro. */
export type BroadcastEstado = 'enviado' | 'fallido' | 'omitido'

export interface BroadcastTarget {
  id: string
  nombre: string
  /**
   * Cuenta emisora en Hermes. Tiene que ser BAILEYS si el destino es un grupo
   * o un canal de WhatsApp.
   */
  chatbotId: string
  /**
   * El `@g.us` del grupo, el `@newsletter` de un canal de WhatsApp (que se pega
   * a mano: no se pueden listar), o un teléfono.
   */
  destino: string
  /** Repos cuyas notas recibe. **Vacío = todos.** */
  productos: string[]
  /** Texto con marcadores; `null` = el texto por defecto del servidor. */
  plantilla: string | null
  activo: boolean
  createdAt: string
  updatedAt: string
}

export interface BroadcastTargetInput {
  nombre: string
  chatbotId: string
  destino: string
  productos: string[]
  plantilla: string | null
  activo: boolean
}

export interface BroadcastLog {
  id: string
  repo: string
  version: string
  estado: BroadcastEstado
  motivo: string | null
  enviadoEn: string
  target: { id: string; nombre: string } | null
}

export interface BroadcastLogsResult {
  items: BroadcastLog[]
  total: number
  page: number
  pageSize: number
}

export interface ProbarResultado {
  id: string
  estado: BroadcastEstado
  motivo: string | null
}

/**
 * Error del API que **conserva el código HTTP**.
 *
 * 🚨 Hace falta por el lockstep de §6.1: el servidor se sube antes que la web,
 * pero entre un despliegue y otro la web puede quedarse hablando con un
 * servidor que todavía no tiene `/broadcast/*`. Sin el código, un 404 de
 * «endpoint que aún no existe» es indistinguible de «no se pudo cargar» y la
 * pantalla no puede explicar lo que está pasando.
 *
 * ⚠️ El campo va declarado y asignado a mano: `erasableSyntaxOnly` está activo
 * en este repo, así que una propiedad de constructor (`constructor(readonly
 * status: number)`) no compila — y `tsc --noEmit` la deja pasar; quien la caza
 * es el `tsc -b` del build.
 */
export class BroadcastApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'BroadcastApiError'
    this.status = status
  }
}

/** `true` cuando el servidor aún no conoce estos endpoints (lockstep). */
export function esServidorViejo(error: unknown): boolean {
  return error instanceof BroadcastApiError && error.status === 404
}

function authHeaders(): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/**
 * Desenvuelve la respuesta y **rescata el mensaje del servidor** cuando lo hay:
 * el 400 del DTO explica por qué un destino no vale, y repetirlo tal cual es
 * mucho más útil que un «no se pudo guardar» genérico.
 */
async function json<T>(res: Response, ctx: string): Promise<T> {
  const texto = await res.text()
  const cuerpo: unknown = texto.trim() ? JSON.parse(texto) : null

  if (!res.ok) {
    const mensaje = (cuerpo as { message?: string | string[] } | null)?.message
    const detalle = Array.isArray(mensaje) ? mensaje.join('; ') : mensaje
    throw new BroadcastApiError(res.status, detalle || `${ctx}: ${res.status}`)
  }
  return cuerpo as T
}

export async function fetchBroadcastTargets(): Promise<BroadcastTarget[]> {
  const res = await fetch(`${ENDPOINT}/targets`, { headers: authHeaders() })
  return (
    (await json<BroadcastTarget[] | null>(res, 'GET broadcast/targets')) ?? []
  )
}

export async function createBroadcastTarget(
  input: BroadcastTargetInput,
): Promise<BroadcastTarget> {
  const res = await fetch(`${ENDPOINT}/targets`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return json<BroadcastTarget>(res, 'POST broadcast/targets')
}

/**
 * Edición parcial: se manda solo lo que cambia. El interruptor de la tabla
 * manda `{ activo }` a secas y no arrastra el resto de la fila.
 */
export async function updateBroadcastTarget(
  id: string,
  cambios: Partial<BroadcastTargetInput>,
): Promise<BroadcastTarget> {
  const res = await fetch(`${ENDPOINT}/targets/${id}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(cambios),
  })
  return json<BroadcastTarget>(res, 'PUT broadcast/targets/:id')
}

export async function deleteBroadcastTarget(
  id: string,
): Promise<{ id: string }> {
  const res = await fetch(`${ENDPOINT}/targets/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return json<{ id: string }>(res, 'DELETE broadcast/targets/:id')
}

/** Manda un WhatsApp de prueba a ese destino y devuelve cómo fue. */
export async function probarBroadcastTarget(
  id: string,
): Promise<ProbarResultado> {
  const res = await fetch(`${ENDPOINT}/targets/${id}/probar`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return json<ProbarResultado>(res, 'POST broadcast/targets/:id/probar')
}

export async function fetchBroadcastLogs(params: {
  page?: number
  pageSize?: number
  targetId?: string
}): Promise<BroadcastLogsResult> {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.targetId) query.set('targetId', params.targetId)

  const res = await fetch(`${ENDPOINT}/logs?${query.toString()}`, {
    headers: authHeaders(),
  })
  return json<BroadcastLogsResult>(res, 'GET broadcast/logs')
}

/* ------------------------------------------------------------------ *
 * Lo que Hermes ofrece para no escribir identificadores a mano
 * (goal `difusion-de-novedades 5` en el servidor, 6 en esta pantalla).
 * ------------------------------------------------------------------ */

/** Una cuenta de plataforma que Hermes ofrece como emisora. */
export interface CuentaEmisora {
  chatbotId: string
  /** Rótulo compuesto por HERMES. El portal lo pinta, no lo interpreta. */
  rotulo: string
  numero: string
  creada: boolean
  vinculada: boolean
  /** Sesión viva en la réplica de Hermes que contestó. */
  conectada: boolean
  /** Puede entregar a un grupo (`@g.us`). Falso en Kapso. */
  admiteGrupos: boolean
}

/**
 * Qué es cada entrada de la lista (goal `difusion-de-novedades 7`). Para
 * WhatsApp una comunidad son varios `@g.us`: la comunidad en sí —que NO recibe
 * mensajes—, su canal de anuncios —lo que se elige para anunciarle— y sus
 * grupos. `desconocida` es «el servidor no lo dijo»: un portal-server o un
 * hermes anteriores a ese goal, o una clase que esta web no conoce.
 *
 * 🚨 Ausente ≠ grupo. Afirmar «es un grupo» de algo que puede ser la comunidad
 * padre es justo el error que el goal cierra: con `desconocida` se ofrece todo
 * sin rótulo, como antes.
 */
export type ClaseDeGrupo =
  | 'comunidad'
  | 'canal_de_anuncios'
  | 'grupo_de_comunidad'
  | 'grupo'
  | 'desconocida'

/** Un grupo de WhatsApp: el `@g.us` que se guarda, su nombre y qué es. */
export interface GrupoDeWhatsapp {
  id: string
  nombre: string
  clase: ClaseDeGrupo
  /** Para el canal de anuncios y los grupos de comunidad; `null` en el resto. */
  comunidad: { id: string; nombre: string } | null
}

/**
 * Envoltorio de las dos lecturas. **Una lista vacía nunca viaja sin motivo.**
 *
 * 🚨 El servidor no devuelve 500 aunque Hermes esté caído (fail-open del goal
 * 5): contesta `disponible: false` con su motivo escrito, y esta pantalla lo
 * usa para caer al texto a mano diciendo por qué.
 */
export interface RespuestaDeHermes<T> {
  disponible: boolean
  /**
   * `null` cuando `disponible` es `true`. Es un `string` a propósito: son los
   * motivos del portal (`SIN_CONFIGURAR`, `SIN_CONEXION`) **y los de Hermes**
   * (`CUENTA_NO_ENCONTRADA`, `SIN_SESION`, `DESCONECTADA`, `CANAL_SIN_GRUPOS`,
   * `ERROR_DEL_SOCKET`), que viajan tal cual. Cerrar la unión aquí obligaría a
   * desplegar la web para que Hermes pudiera añadir un motivo nuevo.
   */
  motivo: string | null
  /** El motivo escrito, para enseñarlo tal cual. */
  detalle: string | null
  items: T[]
}

/** Sobre crudo tal y como llega del portal, antes de normalizar. */
interface SobreCrudo {
  disponible?: boolean
  motivo?: string | null
  detalle?: string | null
  items?: unknown[]
}

const texto = (valor: unknown): string =>
  typeof valor === 'string' ? valor : ''

const CLASES_CONOCIDAS: ReadonlySet<string> = new Set<ClaseDeGrupo>([
  'comunidad',
  'canal_de_anuncios',
  'grupo_de_comunidad',
  'grupo',
])

/** La clase tal y como llega; lo que no se conoce es `desconocida`. */
const claseDeGrupo = (valor: unknown): ClaseDeGrupo =>
  typeof valor === 'string' && CLASES_CONOCIDAS.has(valor)
    ? (valor as ClaseDeGrupo)
    : 'desconocida'

const comunidadDe = (valor: unknown): GrupoDeWhatsapp['comunidad'] => {
  if (!valor || typeof valor !== 'object') return null
  const bruto = valor as Record<string, unknown>
  const id = texto(bruto.id)
  return id ? { id, nombre: texto(bruto.nombre) } : null
}

/**
 * Lee una de las dos listas y **normaliza todo lo que entra**.
 *
 * ⚠️ No es paranoia: el sobre lo compone el portal desde un proto3 leído con
 * `defaults: false`, donde un `false` y un `''` llegan como `undefined`. Dar
 * por bueno un booleano ausente haría pasar «no se pudo preguntar» por «no
 * está en ningún grupo», que es justo el error que deja a alguien esperando un
 * anuncio que nunca sale.
 */
async function leerDeHermes<T>(
  ruta: string,
  ctx: string,
  normalizar: (bruto: Record<string, unknown>) => T,
): Promise<RespuestaDeHermes<T>> {
  const res = await fetch(`${ENDPOINT}${ruta}`, { headers: authHeaders() })
  const cuerpo = await json<SobreCrudo | null>(res, ctx)
  const items = Array.isArray(cuerpo?.items) ? cuerpo.items : []

  return {
    disponible: cuerpo?.disponible === true,
    motivo: texto(cuerpo?.motivo) || null,
    detalle: texto(cuerpo?.detalle) || null,
    items: items.map((bruto) =>
      normalizar((bruto ?? {}) as Record<string, unknown>),
    ),
  }
}

/** Las cuentas de plataforma que pueden emitir la difusión. */
export function fetchEmisoras(): Promise<RespuestaDeHermes<CuentaEmisora>> {
  return leerDeHermes('/emisoras', 'GET broadcast/emisoras', (bruto) => ({
    chatbotId: texto(bruto.chatbotId),
    rotulo: texto(bruto.rotulo),
    numero: texto(bruto.numero),
    creada: bruto.creada === true,
    vinculada: bruto.vinculada === true,
    conectada: bruto.conectada === true,
    admiteGrupos: bruto.admiteGrupos === true,
  }))
}

/**
 * Los grupos de una cuenta emisora. El `chatbotId` va por **query**: es un
 * identificador ajeno (lo acuña Hermes) y no se mete en un segmento de ruta.
 */
export function fetchGruposDeEmisora(
  chatbotId: string,
): Promise<RespuestaDeHermes<GrupoDeWhatsapp>> {
  const query = new URLSearchParams({ chatbotId })
  return leerDeHermes(
    `/emisoras/grupos?${query.toString()}`,
    'GET broadcast/emisoras/grupos',
    (bruto) => ({
      id: texto(bruto.id),
      nombre: texto(bruto.nombre),
      clase: claseDeGrupo(bruto.clase),
      comunidad: comunidadDe(bruto.comunidad),
    }),
  )
}
